// src/utils/captionsSocial.js
//
// The captions screen's client for POST /api/captions/social (dobiecore-remix-worker,
// handleCaptionsSocial). Kept free of React, Vite and Supabase (everything is
// injected) so it can be tested in plain Node; api.js wires it to the worker and the
// caller's token.
//
// The browser sends exactly three things: the brand it picked, the brief, and one
// platform. The worker resolves the brand's workspace, the caller's authority and the
// brand's approved business context itself, so nothing else -- voice profile, tone,
// audience, company name, workspace, or a claim that context was approved -- is sent,
// whatever the caller passes in.
//
// Fail closed: captions are returned only when the worker answers 200 with exactly
// three captions, unique by id and by text, and the id of the caption-history record
// that saved them. Anything else is a CaptionsSocialError with a fixed, safe message;
// the worker's response text is never shown.
//
// The brand picker lists what GET /api/captions/brands returns (handleCaptionBrands):
// the brands the worker's own caption rule lets this caller generate for, as id and
// name only. The list is a convenience; the caption route re-authorizes every request.

export const MAX_BRIEF_CHARS = 500; // the worker's MAX_OFFER_CHARS

// The platforms the route accepts (the worker's CAPTION_PLATFORMS), in screen order.
export const CAPTION_PLATFORMS = [
  { key: "instagram", label: "Instagram" },
  { key: "facebook", label: "Facebook" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "x", label: "X (Twitter)" },
  { key: "tiktok", label: "TikTok" },
  { key: "threads", label: "Threads" },
];
export const DEFAULT_PLATFORM = "instagram";

const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const MESSAGES = {
  brand_required: "Choose a brand to write captions for.",
  offer_required: `Describe what you're promoting in ${MAX_BRIEF_CHARS} characters or fewer.`,
  unsupported_platform: "Choose one of the listed platforms.",
  session_expired: "Your session has expired. Sign in again to create captions.",
  not_permitted: "You don't have permission to create captions for this brand.",
  workspace_not_active: "This brand's workspace isn't active, so captions can't be created for it.",
  context_incomplete:
    "This brand's approved business details aren't complete yet, so captions can't be created for it.",
  generation_not_saved: "Your captions couldn't be saved, so they weren't shown. Please try again.",
  captions_failed: "The captions that came back weren't usable. Please try again.",
  free_limit_reached:
    "You've used today's 2 free caption requests. Free requests reset at midnight UTC; Pro accounts are unlimited.",
  unavailable: "Captions are temporarily unavailable. Please try again in a few minutes.",
  network: "Couldn't reach DobieCore. Check your connection and try again.",
  unconfirmed: "Your captions couldn't be confirmed as saved, so they weren't shown. Please try again.",
  brands_failed: "Your brands couldn't be loaded. Please try again.",
  failed: "Something went wrong creating captions. Please try again.",
};

// The worker's error codes, as this screen reports them.
const SERVER_CODES = {
  invalid_json: "failed",
  brand_required: "brand_required",
  offer_required: "offer_required",
  unsupported_platform: "unsupported_platform",
  missing_token: "session_expired",
  invalid_token: "session_expired",
  not_authenticated_role: "session_expired",
  not_permitted: "not_permitted",
  workspace_not_active: "workspace_not_active",
  context_incomplete: "context_incomplete",
  generation_not_saved: "generation_not_saved",
  captions_failed: "captions_failed",
  free_limit_reached: "free_limit_reached",
  captions_unavailable: "unavailable",
  authorization_unavailable: "unavailable",
  auth_unavailable: "unavailable",
};

export class CaptionsSocialError extends Error {
  constructor(code, status = null) {
    super(MESSAGES[code] ?? MESSAGES.failed);
    this.name = "CaptionsSocialError";
    this.code = MESSAGES[code] ? code : "failed";
    this.status = status;
  }
}

/** A platform key the route accepts, from a key or a screen label (older drafts). */
export function normalizePlatform(value) {
  const v = String(value ?? "").trim().toLowerCase();
  if (v === "twitter" || v === "x (twitter)") return "x";
  return CAPTION_PLATFORMS.some((p) => p.key === v) ? v : DEFAULT_PLATFORM;
}

/**
 * The brand to preselect: the remembered one while it is still in the list, else the
 * first (the worker lists the most recently updated first); "" when there are none.
 */
export function pickBrandId(brands, rememberedId) {
  const list = Array.isArray(brands) ? brands : [];
  if (rememberedId && list.some((b) => b?.id === rememberedId)) return rememberedId;
  return list[0]?.id ?? "";
}

/**
 * The picker's brands from a GET /api/captions/brands body, or null when the body is
 * not { brands: [...] }. Each brand is rebuilt as exactly { id, name }: entries without
 * a canonical id are dropped, repeated ids keep their first entry, and a name that is
 * not a string becomes null. Nothing else the body carries is kept.
 */
export function parseCaptionBrands(body) {
  if (!body || typeof body !== "object" || !Array.isArray(body.brands)) return null;
  const seen = new Set();
  const brands = [];
  for (const item of body.brands) {
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const { id, name } = item;
    if (typeof id !== "string" || !CANONICAL_UUID.test(id) || seen.has(id)) continue;
    seen.add(id);
    brands.push({ id, name: typeof name === "string" ? name : null });
  }
  return brands;
}

/** The request body, exactly { brand_id, offer, platform }, or a CaptionsSocialError. */
export function buildCaptionsSocialBody({ brandId, brief, platform }) {
  if (typeof brandId !== "string" || !CANONICAL_UUID.test(brandId)) {
    throw new CaptionsSocialError("brand_required");
  }
  const offer = typeof brief === "string" ? brief.trim() : "";
  if (!offer || offer.length > MAX_BRIEF_CHARS) throw new CaptionsSocialError("offer_required");
  const key = String(platform ?? "").trim().toLowerCase();
  if (!CAPTION_PLATFORMS.some((p) => p.key === key)) {
    throw new CaptionsSocialError("unsupported_platform");
  }
  return { brand_id: brandId, offer, platform: key };
}

/**
 * The saved result, or null: exactly three captions with non-empty ids (64 chars at
 * most) and non-empty text, unique by id and by trimmed, case-folded text -- the
 * worker's own caption contract -- plus a canonical history_id.
 */
export function parseCaptionsSocialResult(body) {
  if (!body || typeof body !== "object") return null;
  const historyId = body.history_id;
  if (typeof historyId !== "string" || !CANONICAL_UUID.test(historyId)) return null;
  const list = body.captions;
  if (!Array.isArray(list) || list.length !== 3) return null;
  const captions = [];
  for (const item of list) {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;
    const { id, text } = item;
    if (typeof id !== "string" || !id || id.length > 64) return null;
    if (typeof text !== "string" || !text.trim()) return null;
    captions.push({ id, text });
  }
  if (new Set(captions.map((c) => c.id)).size !== 3) return null;
  if (new Set(captions.map((c) => c.text.trim().toLowerCase())).size !== 3) return null;
  return { captions, historyId };
}

function errorFor(status, body) {
  const server = typeof body?.error === "string" ? SERVER_CODES[body.error] : undefined;
  if (server) return new CaptionsSocialError(server, status);
  if (status === 401) return new CaptionsSocialError("session_expired", status);
  if (status === 404 || status >= 500) return new CaptionsSocialError("unavailable", status);
  return new CaptionsSocialError("failed", status);
}

/**
 * @param {object} deps
 * @param {string} deps.endpoint                 the worker's /api/captions/social URL
 * @param {typeof fetch} deps.fetch
 * @param {() => Promise<object>} deps.headers   JSON headers with the caller's token
 * @returns {(request: {brandId: string, brief: string, platform: string}) =>
 *            Promise<{captions: {id: string, text: string}[], historyId: string}>}
 */
export function createSocialCaptionsClient({ endpoint, fetch, headers }) {
  return async function generateSocialCaptions({ brandId, brief, platform }) {
    const body = buildCaptionsSocialBody({ brandId, brief, platform });

    let response;
    try {
      response = await fetch(endpoint, {
        method: "POST",
        headers: await headers(),
        body: JSON.stringify(body),
      });
    } catch {
      throw new CaptionsSocialError("network");
    }

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    if (response.status !== 200) throw errorFor(response.status, data);

    const result = parseCaptionsSocialResult(data);
    if (!result) throw new CaptionsSocialError("unconfirmed", response.status);
    return result;
  };
}

function brandsErrorFor(status) {
  if (status === 401 || status === 403) return new CaptionsSocialError("session_expired", status);
  if (status === 404 || status >= 500) return new CaptionsSocialError("unavailable", status);
  return new CaptionsSocialError("brands_failed", status);
}

/**
 * @param {object} deps
 * @param {string} deps.endpoint                 the worker's /api/captions/brands URL
 * @param {typeof fetch} deps.fetch
 * @param {() => Promise<object>} deps.headers   headers with the caller's token
 * @returns {() => Promise<{id: string, name: string|null}[]>}
 */
export function createCaptionBrandsClient({ endpoint, fetch, headers }) {
  return async function listCaptionBrands() {
    let response;
    try {
      response = await fetch(endpoint, { method: "GET", headers: await headers() });
    } catch {
      throw new CaptionsSocialError("network");
    }
    if (response.status !== 200) throw brandsErrorFor(response.status);

    let data = null;
    try {
      data = await response.json();
    } catch {
      data = null;
    }
    const brands = parseCaptionBrands(data);
    if (!brands) throw new CaptionsSocialError("brands_failed", response.status);
    return brands;
  };
}
